/**
 * User Management Table Component
 *
 * Displays a table of users with their information and status badges.
 * Supports loading states, empty states, and error handling.
 */

import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { User, UserLatestDevice } from '@/types/user-management';

interface UserManagementTableProps {
  users: User[];
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  onUserClick: (user: User) => void;
}

/**
 * Format Date
 * Formats an ISO date string to a readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatLatestBuild(latestDevice: UserLatestDevice | null | undefined): string {
  if (!latestDevice || !latestDevice.appVersion) return '—';
  const platform = latestDevice.platform.toUpperCase();
  const version = latestDevice.appVersion;
  const build = latestDevice.appBuild ? ` (${latestDevice.appBuild})` : '';
  return `${platform} ${version}${build}`;
}

/**
 * User Management Table
 * Renders a table of users with status badges and click handling
 */
export function UserManagementTable({
  users,
  isLoading,
  error,
  onRetry,
  onUserClick,
}: UserManagementTableProps) {
  // Loading State
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-2">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading users...</p>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h3 className="text-lg font-semibold">Failed to load users</h3>
          <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
        </div>
        <Button onClick={onRetry} variant="outline">
          Try Again
        </Button>
      </div>
    );
  }

  // Empty State
  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-2">
        <h3 className="text-lg font-semibold">No users found</h3>
        <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
      </div>
    );
  }

  // Table Content
  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Created Date</TableHead>
            <TableHead>Latest Build</TableHead>
            <TableHead>Olo Status</TableHead>
            <TableHead>Punchh Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map(user => (
            <TableRow
              key={user.id}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onUserClick(user)}
            >
              <TableCell className="font-medium">
                {user.firstName} {user.lastName}
              </TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{user.phone}</TableCell>
              <TableCell>{formatDate(user.createdAt)}</TableCell>
              <TableCell className="font-mono text-xs">
                {formatLatestBuild(user.latestDevice)}
              </TableCell>
              <TableCell>
                {user.oloId ? (
                  <Badge variant="success">Linked</Badge>
                ) : (
                  <Badge variant="secondary">Not Linked</Badge>
                )}
              </TableCell>
              <TableCell>
                {user.punchId ? (
                  <Badge variant="success">Linked</Badge>
                ) : (
                  <Badge variant="secondary">Not Linked</Badge>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
